import { Fragment, lazy, type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { useStudyCreateStep, useStudyCreateGroups } from '@/utils/studyCreation/useStudyCreate.tsx';
import Banner from '@/components/UI/Banner';
import Radio from '@/components/UI/FormElements/Radio';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model.ts';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import type HotTable from '@handsontable//react';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { _isEmptyString, _isNotEmpty, _notNil } from '@/littledash.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import { preventNumberScroll } from '@/helpers.tsx';
import { MetadataField } from '@/model/Metadata.model.ts';
import {
  generateInitialAnimals,
  generateAnimalColumns,
  convertToPlannedAnimals,
  getColumnData,
  handleMetadataChange,
  patchMetadata,
  mapMetadataResponse,
  MetadataGlossaryItem,
  updateTmpIds,
  createPlannedAnimalData,
  type AnimalRow,
  bannerMessageMap,
} from './Animals.utils';
import {
  convertToPlannedAnimalsV16,
  generateAnimalColumnsV16,
  generateInitialAnimalsV16,
  getColumnDataV16,
  patchMetadataV16,
} from './Animals.utils.v16';
import { PlannedAnimal, PlannedAnimalData, PlanTypeMap, PlanTypes } from '@/model/PlannedAnimal.model.ts';
import { ID } from '@/model/Common.model';
import { FormProvider, useForm } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import { notAborted } from '@/support/Hooks/fetch/useAbortController.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema.tsx';
import Button from '@/components/UI/Button';
import Loading from '@/components/Loading';
import { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { featuresSelector } from '@/support/Selectors';

const AnimalsTableV12 = lazy(() => import('./AnimalsTableV12'));
const AnimalsTableV16 = lazy(() => import('./AnimalsTableV16'));

interface AnimalsPerCageData {
  animalsPerCage: number | null;
}

const groupIdAccessor = (group: TreatmentGroup) => group.temp_id as string;

const selector = createSelector([featuresSelector], (features) => ({
  atunesIntegrationEnabled: features?.atunes_colony_management_integration === true,
  hotUpgrade: features?.hot_upgrade ?? false,
  unknownSexEnabled: features?.unknown_sex === true,
}));

const Animals = () => {
  const { setStepReady } = useStudyCreateStep();
  const { groups, plannedAnimalData, setPlannedAnimalData } = useStudyCreateGroups();
  const { atunesIntegrationEnabled, hotUpgrade, unknownSexEnabled } = useSelector(selector);

  const [species, setSpecies] = useState<Array<SpeciesV1>>([]);
  const [animalData, setAnimalData] = useState<Array<AnimalRow>>([]);
  const [animalDataCache, setAnimalDataCache] = useState<Array<AnimalRow>>([]);
  const [studyMetadata, setStudyMetadata] = useState<Array<MetadataField>>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<Array<MetadataField>>([]);
  const [availableMetadata, setAvailableMetadata] = useState<Array<MetadataField>>([]);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [bannerMessage, setBannerMessage] = useState<string>();
  const ref = useRef<HotTable>();
  const refV16 = useRef<HotTableRef>(null);

  const plannedAnimals: Array<PlannedAnimal> = useMemo(() => {
    switch (plannedAnimalData.type) {
      case 'planned':
      case 'registered':
        return plannedAnimalData.animals ?? [];
      case 'skip':
        return [];
    }
  }, [plannedAnimalData]);
  const { totalGroupCapacity, maxRows } = useMemo(() => {
    const totalGroupCapacity = groups.reduce((acc, group) => acc + group.max_subjects, 0);
    return { totalGroupCapacity, maxRows: Math.max(totalGroupCapacity, plannedAnimals.length) };
  }, [groups, plannedAnimals.length]);
  const [planType, setPlanType] = useState<PlanTypes>(
    maxRows > 0 ? (plannedAnimalData.type ?? PlanTypeMap.planned) : PlanTypeMap.skip
  );
  const [animalsPerCage, setAnimalsPerCage] = useState<number | null>(
    plannedAnimalData.type === PlanTypeMap.registered ? (plannedAnimalData.animalsPerCage ?? 5) : 5
  );

  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const dobRequired = entityFieldRequiredMap?.dob ?? true;
  const columnWidthClass = atunesIntegrationEnabled ? 'w-50' : 'w-33';

  const buildInitialAnimals = useCallback(
    () =>
      hotUpgrade
        ? generateInitialAnimalsV16(groups, dobRequired, selectedMetadata)
        : generateInitialAnimals(groups, dobRequired),
    [hotUpgrade, groups, dobRequired, selectedMetadata]
  );

  const buildAnimalColumnsFromPlanned = useCallback(
    (animals: Array<PlannedAnimal>, pt: PlanTypes) =>
      hotUpgrade
        ? generateAnimalColumnsV16(animals, groups, pt, dobRequired, selectedMetadata)
        : generateAnimalColumns(animals, groups, pt, dobRequired),
    [hotUpgrade, groups, dobRequired, selectedMetadata]
  );

  const convertRowsToPlannedAnimalData = useCallback(
    (rows: Array<AnimalRow>, perCage: number | null, pt: PlanTypes) =>
      hotUpgrade
        ? convertToPlannedAnimalsV16(rows, groups, species, perCage, pt, groupIdAccessor, selectedMetadata)
        : convertToPlannedAnimals(rows, groups, species, perCage, pt, groupIdAccessor),
    [hotUpgrade, groups, species, selectedMetadata]
  );

  useEffect(() => {
    if (_isNotEmpty(groups) && planType !== PlanTypeMap.skip) {
      if (plannedAnimals.length > 0) {
        setAnimalData(buildAnimalColumnsFromPlanned(plannedAnimals, planType));
        setBannerMessage(
          totalGroupCapacity > plannedAnimals.length ? bannerMessageMap.availableCapacity : bannerMessageMap.baseMessage
        );
      } else {
        setAnimalData(buildInitialAnimals());
        setBannerMessage(bannerMessageMap.baseMessage);
      }
    }
  }, [
    groups,
    plannedAnimals,
    planType,
    dobRequired,
    totalGroupCapacity,
    buildAnimalColumnsFromPlanned,
    buildInitialAnimals,
  ]);

  const formMethods = useForm<AnimalsPerCageData>({
    defaultValues: {
      animalsPerCage: 5,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const {
    register,
    formState: { errors },
  } = formMethods;

  useEffect(() => {
    setStepReady(true);
  }, [setStepReady]);

  const { invoke: fetchSpecies } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: false,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const { invoke: fetchMetadata } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'subject_meta',
    },
    invokeOnInit: false,
  });

  const updatePlannedAnimals = useCallback(
    (speciesData: Array<SpeciesV1>, metadataData: Array<MetadataField>, plannedAnimals: Array<PlannedAnimal>) => {
      const usedMetadataIds = new Set<ID>();
      let usedMetadataFields: Array<MetadataField> = [];
      if (_isNotEmpty(plannedAnimals)) {
        const updatedPlannedAnimals = plannedAnimals.map((animal) => {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (animal.strainApiId) {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            const speciesMatch = speciesData.find((s) =>
              s.strains?.some((strain) => strain.api_id === animal.strainApiId)
            );
            const strainMatch = speciesMatch?.strains?.find((strain) => strain.api_id === animal.strainApiId);
            return { ...animal, species: speciesMatch?.name, strain: strainMatch?.name };
          }
          return animal;
        });

        updatedPlannedAnimals.forEach((animal) => {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (animal.metadata) {
            Object.keys(animal.metadata).forEach((key) => {
              usedMetadataIds.add(key);
              const glossaryId = animal.metadata?.[key]?.glossary_id;
              // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
              if (glossaryId) {
                usedMetadataIds.add(glossaryId);
              }
            });
          }
        });
        usedMetadataFields = metadataData.filter((field) => usedMetadataIds.has(field.glossary_id));
        const finalUpdatedAnimals = updatedPlannedAnimals.map((animal) => {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (!animal.metadata) {
            return animal;
          }
          const updatedMetadata = { ...animal.metadata };
          Object.entries(animal.metadata).forEach(([key, value]) => {
            const matchingField = metadataData.find((field) => field.glossary_id === value.glossary_id);
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (matchingField && updatedMetadata[key]) {
              updatedMetadata[key] = {
                ...updatedMetadata[key],
                glossary_api_id: matchingField.glossary_api_id,
              };
            }
          });
          return {
            ...animal,
            metadata: updatedMetadata,
          };
        });
        const updatedPlannedAnimalData = createPlannedAnimalData(finalUpdatedAnimals, planType, animalsPerCage);
        setPlannedAnimalData(updatedPlannedAnimalData);
      }
      setSelectedMetadata(usedMetadataFields);
      const remainingMetadata = metadataData.filter((field) => {
        return !usedMetadataIds.has(field.glossary_id);
      });
      setAvailableMetadata(remainingMetadata);
    },
    [planType, animalsPerCage, setPlannedAnimalData]
  );

  const initialise = async () => {
    try {
      const [speciesResponse, metadataResponse] = await Promise.all([fetchSpecies(), fetchMetadata()]);
      if (speciesResponse?.type === 'success' && metadataResponse?.type === 'success') {
        const speciesData = speciesResponse.body.data;
        const metadataResponseData: Array<MetadataField> = mapMetadataResponse(
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          (metadataResponse?.body || []) as Array<MetadataGlossaryItem>
        );
        setSpecies(speciesData);
        setStudyMetadata(metadataResponseData);
        updatePlannedAnimals(speciesData, metadataResponseData, plannedAnimals);
      }
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error);
      }
    }
  };

  useEffect(() => {
    initialise();
  }, []);

  const columnData = useMemo(() => {
    if (hotUpgrade) {
      return getColumnDataV16(
        species,
        selectedMetadata,
        groups,
        groupIdAccessor,
        planType,
        true,
        dobRequired,
        [],
        unknownSexEnabled
      );
    }
    return getColumnData(
      species,
      selectedMetadata,
      groups,
      groupIdAccessor,
      ref.current?.hotInstance ?? null,
      planType,
      true,
      dobRequired,
      [],
      unknownSexEnabled
    );
  }, [
    species,
    selectedMetadata,
    groups,
    planType,
    ref.current?.hotInstance,
    dobRequired,
    hotUpgrade,
    unknownSexEnabled,
  ]);

  const settings = useMemo<CustomGridSettings>(() => {
    return {
      ...columnData,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      rowHeaders: true,
      outsideClickDeselects: true,
      allowInsertRow: true,
      allowInsertColumn: false,
      allowRemoveRow: true,
      minSpareRows: 0,
      columnSorting: false,
      className: 'htMiddle',
      manualColumnResize: true,
      rowHeights: 50,
      height: animalData.length <= 30 ? 'auto' : 1500,
    };
  }, [columnData, animalData.length]);

  const settingsV16 = useMemo<SpreadsheetV16Settings>(() => {
    return {
      ...columnData,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      rowHeaders: true,
      outsideClickDeselects: true,
      minSpareRows: 0,
      columnSorting: false,
      className: 'htMiddle',
      manualColumnResize: true,
      rowHeights: 50,
      height: animalData.length <= 30 ? 'auto' : 1500,
    };
  }, [columnData, animalData.length]);

  const afterChangeIncludeSources = useMemo(
    () => new Set(['edit', 'Autofill.fill', 'CopyPaste.paste', 'UndoRedo.undo', 'UndoRedo.redo']),
    []
  );

  const handleDataChange = (changes: unknown, source: string) => {
    if (hotUpgrade) {
      if (_notNil(source) && !afterChangeIncludeSources.has(source)) {
        return;
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (!changes || source === 'loadData') {
        return;
      }
    }
    const hotInstance = hotUpgrade ? (refV16.current?.hotInstance ?? null) : (ref.current?.hotInstance ?? null);
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (hotInstance) {
      const updatedAnimalData: Array<AnimalRow> = hotInstance.getSourceData() as Array<AnimalRow>;
      const updatedPlannedAnimalData = convertRowsToPlannedAnimalData(updatedAnimalData, animalsPerCage, planType);
      setPlannedAnimalData(updatedPlannedAnimalData);
    }
  };

  const hasAnimalsPerCageError = _notNil(errors.animalsPerCage);

  const syncPlannedAnimalsAndValidateV16 = useCallback(() => {
    const hotInstance = refV16.current?.hotInstance;
    if (!_notNil(hotInstance)) {
      return;
    }
    const updatedAnimalData = hotInstance.getSourceData() as Array<AnimalRow>;
    setPlannedAnimalData(convertRowsToPlannedAnimalData(updatedAnimalData, animalsPerCage, planType));
    hotInstance.validateCells((valid: boolean) => {
      setStepReady(valid && !hasAnimalsPerCageError);
    });
  }, [
    animalsPerCage,
    planType,
    hasAnimalsPerCageError,
    setStepReady,
    setPlannedAnimalData,
    convertRowsToPlannedAnimalData,
  ]);

  useEffect(() => {
    if (animalData.length > 0) {
      window.requestAnimationFrame(() => {
        const hotInstance = hotUpgrade ? refV16.current?.hotInstance : ref.current?.hotInstance;
        if (_notNil(hotInstance) && !(hotInstance as unknown as { isDestroyed: boolean }).isDestroyed) {
          hotInstance.validateCells((valid: boolean) => {
            setStepReady(valid && !hasAnimalsPerCageError);
          });
        }
      });
    }
  }, [hasAnimalsPerCageError, animalData, columnData, setStepReady, hotUpgrade]);

  const patchMetadataCallback = useCallback(() => {
    if (hotUpgrade) {
      patchMetadataV16(refV16.current?.hotInstance ?? null, species);
    } else {
      patchMetadata(ref.current?.hotInstance ?? null, settings, species);
    }
  }, [species, settings, hotUpgrade]);

  const handleMetadataChangeCallback = useCallback(
    (metadata: Array<MetadataField>) => {
      handleMetadataChange(metadata, studyMetadata, setSelectedMetadata, setAvailableMetadata);
    },
    [studyMetadata, setSelectedMetadata, setAvailableMetadata]
  );

  const validateNumber = useCallback(
    (value: any) => {
      let errorMsg: string = '';
      if (isNaN(value)) {
        errorMsg = 'Must be a valid number';
      }
      if (value <= 0) {
        errorMsg = 'Must be greater than 0';
      }
      if (value > 15) {
        errorMsg = 'Maximum of 15 animals per cage';
      }
      if (_isNotEmpty(errorMsg)) {
        formMethods.setError('animalsPerCage', { type: 'manual', message: errorMsg });
        setStepReady(false);
      } else {
        formMethods.clearErrors('animalsPerCage');
        setStepReady(true);
      }
    },
    [setStepReady, formMethods]
  );

  const handleValidationErrorMessage = (isValid: boolean, value: ReactNode, row: number, prop: string | number) => {
    if (!isValid) {
      if (prop === 'groupIdentifier') {
        const hot = hotUpgrade ? refV16.current?.hotInstance : ref.current?.hotInstance;
        if (_notNil(hot) && !_isEmptyString(value)) {
          const group = groups.find((g) => g.temp_id === value);
          const errorMsg = _notNil(group)
            ? 'Number of planned animals assigned exceeds maximum subjects allowed: ' + (group?.max_subjects ?? 0)
            : 'Planned animals must be assigned to a group.';
          setBannerMessage(bannerMessageMap.conflicts);
          hot.getCell(row, hot.propToCol('groupIdentifier'))?.setAttribute('data-tooltip-content', errorMsg);
        }
      }
    }
  };

  return (
    <Fragment>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">Animals</h3>
        <p>Assign animals to cages after the study is created.</p>
      </div>
      <div className="w-75 ui-card" data-testid="planned-animals__container">
        {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
        {apiError ? (
          <ApiErrorBanner
            className="mw6 mb4"
            error={apiError}
            title="There was an error fetching animals data"
            text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
          />
        ) : null}
        <div className="pt4 pb2 flex flex-wrap">
          <div className="ph4 flex justify-between w-100">
            <div className={columnWidthClass}>
              <Radio
                id="planAnimals"
                name="planAnimals"
                checked={planType === PlanTypeMap.planned}
                disabled={false}
                value="planAnimals"
                label="Plan animals"
                onChange={() => {
                  setPlanType(PlanTypeMap.planned);
                  formMethods.clearErrors('animalsPerCage');
                  const updatedAnimalData = updateTmpIds(
                    _isNotEmpty(animalData) ? animalData : animalDataCache,
                    PlanTypeMap.planned
                  );
                  const updatedPlannedAnimalData: PlannedAnimalData = convertRowsToPlannedAnimalData(
                    updatedAnimalData,
                    null,
                    PlanTypeMap.planned
                  );
                  setPlannedAnimalData(updatedPlannedAnimalData);
                }}
                className="pb2"
              />
            </div>
            {!atunesIntegrationEnabled && (
              <div className="w-33">
                <Radio
                  id="registerAnimals"
                  name="registerAnimals"
                  checked={planType === PlanTypeMap.registered}
                  disabled={false}
                  value="registerAnimals"
                  label="Register animals"
                  onChange={() => {
                    setPlanType(PlanTypeMap.registered);
                    const updatedAnimalData = updateTmpIds(
                      _isNotEmpty(animalData) ? animalData : animalDataCache,
                      PlanTypeMap.registered
                    );
                    validateNumber(animalsPerCage);
                    const updatedPlannedAnimalData = convertRowsToPlannedAnimalData(
                      updatedAnimalData,
                      animalsPerCage,
                      PlanTypeMap.registered
                    );
                    setPlannedAnimalData(updatedPlannedAnimalData);
                  }}
                  className="pb2"
                />
              </div>
            )}
            <div className={columnWidthClass}>
              <Radio
                id="skipAnimals"
                name="skipAnimals"
                checked={planType === PlanTypeMap.skip}
                disabled={false}
                value="skipAnimals"
                label="Skip animals"
                onChange={() => {
                  setPlanType(PlanTypeMap.skip);
                  setAnimalDataCache(animalData);
                  const updatedPlannedAnimalData = convertRowsToPlannedAnimalData([], null, PlanTypeMap.skip);
                  setPlannedAnimalData(updatedPlannedAnimalData);
                  setStepReady(true);
                }}
                className="pb2"
              />
            </div>
          </div>
          <div className="ph4 mb3 flex justify-between w-100">
            <div className={columnWidthClass}>
              <p className="f6 pl4 w-90">
                Create placeholder IDs to allow sample planning and tube labelling without registering animals.
              </p>
            </div>
            {!atunesIntegrationEnabled && (
              <div className="w-33">
                <p className="f6 pl4 w-90">
                  Register animals and assign to cages. Cage names and metadata can be updated afterward.
                </p>
              </div>
            )}
            <div className={columnWidthClass}>
              <p className="f6 pl4 w-90">Animals and cages can be added after the study is created.</p>
            </div>
          </div>
        </div>
        <Banner info dismiss={false} className="mb4">
          <div className="flex justify-between w-100">
            <h3 className="f6 normal lh-title">{bannerMessage}</h3>
            {planType !== 'skip' && (
              <Button
                plain
                onClick={() => {
                  const updatedAnimalData = buildInitialAnimals();
                  setAnimalData(updatedAnimalData);
                  const updatedPlannedAnimalData = convertRowsToPlannedAnimalData(
                    updatedAnimalData,
                    animalsPerCage,
                    planType
                  );
                  setPlannedAnimalData(updatedPlannedAnimalData);
                  setBannerMessage(bannerMessageMap.baseMessage);
                }}
                testId="reset-planned-animals"
                tooltip={'Generate planned animals based on current groups'}
              >
                Reset
              </Button>
            )}
          </div>
        </Banner>
        <div>
          {_isNotEmpty(animalData) && planType !== PlanTypeMap.skip ? (
            <div className="flex flex-wrap justify-center items-center br2 bg-near-whitetc">
              <div
                className={`ui__table-bordered bg-white w-100 ow-spreadsheet-styles${hotUpgrade ? ' ow-spreadsheet-styles-v16' : ''}`}
                data-test-component="ReviewTable"
                data-test-element="container"
              >
                <Suspense fallback={<Loading />}>
                  {hotUpgrade ? (
                    <AnimalsTableV16
                      data={animalData as unknown as Array<Record<string, unknown>>}
                      settings={settingsV16}
                      innerRef={refV16 as React.RefObject<HotTableRef>}
                      metadata={availableMetadata}
                      setMetadata={handleMetadataChangeCallback}
                      patchMetadataAfterSettingsUpdated={patchMetadataCallback}
                      maxRows={maxRows}
                      afterChange={handleDataChange}
                      afterRemoveRow={syncPlannedAnimalsAndValidateV16}
                      afterCreateRow={syncPlannedAnimalsAndValidateV16}
                      afterValidation={handleValidationErrorMessage}
                      modalView={false}
                    />
                  ) : (
                    <AnimalsTableV12
                      data={animalData as unknown as Array<Record<string, unknown>>}
                      settings={settings}
                      innerRef={ref as React.MutableRefObject<HotTable | undefined>}
                      metadata={availableMetadata}
                      setMetadata={handleMetadataChangeCallback}
                      patchMetadataAfterSettingsUpdated={patchMetadataCallback}
                      maxRows={maxRows}
                      afterChange={handleDataChange}
                      afterRemoveRow={() => {
                        handleDataChange('removeRow', '');
                      }}
                      afterCreateRow={() => {
                        handleDataChange('createRow', '');
                      }}
                      afterValidation={handleValidationErrorMessage}
                      modalView={false}
                    />
                  )}
                </Suspense>
              </div>
            </div>
          ) : (
            <div className={'ui-card-hot mv pa4'} style={{ minHeight: 190 }}>
              <div
                className="br2 bg-near-white tc flex items-center justify-center flex-column pa4"
                data-test-component="PlannedAnimalsSetup"
                data-test-element="empty-state"
              >
                <div className="mw6 center ph4 pa4">
                  <h3 className="mb2">No planned animals</h3>
                  {planType !== PlanTypeMap.skip && (
                    <p className="mb3">Create treatment groups to generate planned animals</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <FormProvider {...formMethods}>
          <form>
            {planType === PlanTypeMap.registered && plannedAnimals.length > 0 && (
              <div className="pt3 pl3 pb2 b--moon-gray">
                <label>Animals per Cage</label>
                <div className="flex">
                  <input
                    {...register('animalsPerCage', {
                      required: true,
                    })}
                    type="number"
                    onWheel={preventNumberScroll}
                    style={{ marginBottom: 0, width: '200px' }}
                    className={`mr2`}
                    value={animalsPerCage ?? ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setAnimalsPerCage(value);
                      validateNumber(value);
                      const updatedPlannedAnimalData = convertRowsToPlannedAnimalData(
                        animalData,
                        value,
                        PlanTypeMap.registered
                      );
                      setPlannedAnimalData(updatedPlannedAnimalData);
                    }}
                    data-testid="generate-cage-count-input"
                  />
                </div>
                <p className="f6 pt1 w-90">Cages will be automatically generated upon study creation.</p>
                <ErrorMessage
                  errors={errors}
                  name={`animalsPerCage`}
                  render={({ message }) => <p className="f6 red pt1 w-90">{message}</p>}
                />
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </Fragment>
  );
};

export default Animals;
